import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { FileText, TrendingUp, CheckCircle2, XCircle, Plus, Users, UserPlus } from "lucide-react";
import { format } from "date-fns";

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending review", cls: "bg-amber-100 text-amber-700" },
  document_verified: { label: "Docs verified", cls: "bg-violet-100 text-violet-700" },
  course_assigned: { label: "Course assigned", cls: "bg-violet-100 text-violet-700" },
  eligible_for_exam: { label: "Eligible for exam", cls: "bg-violet-100 text-violet-700" },
  exception: { label: "Exemption", cls: "bg-violet-100 text-violet-700" },
  sent_for_approval: { label: "Sent for approval", cls: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  paid: { label: "Certified", cls: "bg-green-100 text-green-700" },
  certified: { label: "Certified", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};

const AVATAR_COLORS = ["#1e3a5f", "#6d28d9", "#b45309", "#15803d", "#b91c1c", "#0e7490"];
const avatarColor = (s: string) => AVATAR_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const chipCls = (kind: string) => {
  const base = "grid h-9 w-9 place-items-center rounded-[11px] ";
  switch (kind) {
    case "amber": return base + "gradient-warm text-white shadow";
    case "violet": return base + "bg-violet-100 text-violet-600";
    case "green": return base + "bg-green-100 text-green-600";
    case "red": return base + "bg-red-100 text-red-600";
    default: return base + "bg-muted text-muted-foreground";
  }
};

const GrowLearnDashboard = () => {
  const { fullName } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0 });
  const [recent, setRecent] = useState<any[]>([]);
  const [failed, setFailed] = useState<any[]>([]);

  const fetchAll = async () => {
    const { count: total } = await supabase.from("applications").select("*", { count: "exact", head: true });
    const { count: pending } = await supabase.from("applications").select("*", { count: "exact", head: true }).eq("status", "pending");
    const { count: inProgress } = await supabase.from("applications").select("*", { count: "exact", head: true }).in("status", ["document_verified", "course_assigned", "eligible_for_exam", "exception"]);
    const { count: completed } = await supabase.from("applications").select("*", { count: "exact", head: true }).in("status", ["sent_for_approval", "approved", "paid" as any, "certified"]);
    const { count: failedCount } = await supabase.from("exam_assignments").select("*", { count: "exact", head: true }).eq("status", "failed");
    setStats({ total: total || 0, pending: pending || 0, inProgress: inProgress || 0, completed: completed || 0, failed: failedCount || 0 });

    const { data: apps } = await supabase
      .from("applications")
      .select("id, full_name, email, status, payment_status, created_at")
      .order("created_at", { ascending: false })
      .limit(6);
    setRecent(apps || []);

    const { data: fails } = await supabase
      .from("exam_assignments")
      .select("id, user_id, think_exam_test_name, think_exam_test_id, score, total_marks, completed_at")
      .eq("status", "failed")
      .order("completed_at", { ascending: false })
      .limit(3);
    if (fails && fails.length) {
      const ids = [...new Set(fails.map((f) => f.user_id))];
      const { data: profs } = await supabase.from("profiles").select("user_id, full_name").in("user_id", ids);
      const nameMap = new Map((profs || []).map((p) => [p.user_id, p.full_name]));
      setFailed(fails.map((f) => ({ ...f, name: nameMap.get(f.user_id) || "—" })));
    } else {
      setFailed([]);
    }
  };

  useEffect(() => {
    fetchAll();
    api.post("/think-exam/sync-pending").then(() => fetchAll()).catch(() => {});
    const channel = supabase
      .channel("dashboard-exam-assignments")
      .on("postgres_changes", { event: "*", schema: "public", table: "exam_assignments" }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const firstName = fullName?.split(" ")[0] || "there";
  const completionPct = stats.total ? Math.round((stats.completed / stats.total) * 100) : 0;

  const kpis = [
    { label: "Total Applications", value: stats.total, icon: <FileText className="h-[18px] w-[18px]" />, chip: "amber", sub: "Across all statuses" },
    { label: "In Progress", value: stats.inProgress, icon: <TrendingUp className="h-[18px] w-[18px]" />, chip: "violet", sub: `${stats.pending} pending review` },
    { label: "Completed", value: stats.completed, icon: <CheckCircle2 className="h-[18px] w-[18px]" />, chip: "green", sub: `${completionPct}% completion rate` },
    { label: "Failed Exams", value: stats.failed, icon: <XCircle className="h-[18px] w-[18px]" />, chip: "red", sub: failed.length ? "Need reassignment" : "None to review" },
  ];

  const pipe = [
    { label: "Pending", count: stats.pending, color: "#f59e0b" },
    { label: "In progress", count: stats.inProgress, color: "#6d28d9" },
    { label: "Completed", count: stats.completed, color: "#15803d" },
  ];
  const pipeTotal = pipe.reduce((a, p) => a + p.count, 0) || 1;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#7450a9]">GrowLearn Portal</p>
              <h2 className="mt-2 text-2xl font-bold tracking-tight text-foreground">Welcome back, {firstName}</h2>
              <p className="mt-1 text-sm text-muted-foreground">Here's what's happening across your students</p>
            </div>
            <Link
              to="/growlearn/register"
              className="inline-flex items-center gap-2 rounded-full gradient-warm px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:opacity-95"
            >
              <Plus className="h-4 w-4" /> Register Student
            </Link>
          </div>

          {/* KPIs */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {kpis.map((k) => (
              <div key={k.label} className="glass-panel rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium text-muted-foreground">{k.label}</span>
                  <span className={chipCls(k.chip)}>{k.icon}</span>
                </div>
                <p className="mt-3 text-[34px] font-bold leading-none tracking-tight tabular-nums text-foreground">{k.value}</p>
                <p className="mt-1.5 text-xs text-muted-foreground">{k.sub}</p>
              </div>
            ))}
          </div>

          {/* Main */}
          <div className="grid gap-4 lg:grid-cols-[1.7fr_1fr]">
            {/* Recent applications */}
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h3 className="text-sm font-bold">Recent Applications</h3>
                <Link to="/growlearn/students" className="text-xs font-semibold text-[#7450a9] hover:underline">View all →</Link>
              </div>
              {recent.length === 0 ? (
                <p className="px-5 py-12 text-center text-sm text-muted-foreground">No applications yet</p>
              ) : (
                recent.map((a) => {
                  const pill = a.payment_status === "paid" ? STATUS_PILL.certified : (STATUS_PILL[a.status] || { label: a.status, cls: "bg-muted text-muted-foreground" });
                  return (
                    <div key={a.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border px-5 py-3 last:border-0">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-xs font-bold text-white" style={{ background: avatarColor(a.full_name || a.email || "?") }}>
                          {initials(a.full_name || a.email)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-[13px] font-semibold text-foreground">{a.full_name || "—"}</p>
                          <p className="truncate text-xs text-muted-foreground">{a.email}</p>
                        </div>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${pill.cls}`}>{pill.label}</span>
                      <span className="whitespace-nowrap text-xs text-muted-foreground">{a.created_at ? format(new Date(a.created_at), "MMM d") : ""}</span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {/* Pipeline */}
              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="border-b border-border px-5 py-4"><h3 className="text-sm font-bold">Application Pipeline</h3></div>
                <div className="p-5">
                  <div className="flex h-2.5 overflow-hidden rounded-full bg-muted">
                    {pipe.map((p) => p.count > 0 && <span key={p.label} style={{ width: `${(p.count / pipeTotal) * 100}%`, background: p.color }} />)}
                  </div>
                  <div className="mt-4 grid gap-2.5">
                    {pipe.map((p) => (
                      <div key={p.label} className="flex items-center gap-2.5 text-[13px]">
                        <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: p.color }} />
                        {p.label}
                        <span className="ml-auto font-bold tabular-nums">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Needs attention */}
              {failed.length > 0 && (
                <div className="glass-panel rounded-2xl overflow-hidden">
                  <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <h3 className="text-sm font-bold">Needs Attention</h3>
                    <Link to="/growlearn/failed" className="text-xs font-semibold text-red-600 hover:underline">Failed →</Link>
                  </div>
                  {failed.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 border-b border-border px-5 py-3 last:border-0">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-red-100 text-xs font-bold text-red-700">{initials(f.name)}</span>
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-semibold">{f.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{f.think_exam_test_name || f.think_exam_test_id} · {f.score ?? 0}/{f.total_marks ?? 0}</p>
                      </div>
                      <Link to={`/growlearn/students/${f.user_id}`} className="ml-auto rounded-full bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-200">Reassign</Link>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick actions */}
              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="border-b border-border px-5 py-4"><h3 className="text-sm font-bold">Quick Actions</h3></div>
                <div className="grid gap-2.5 p-3.5">
                  <Link to="/growlearn/students" className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 transition hover:bg-muted">
                    <span className="grid h-9 w-9 place-items-center rounded-[11px] gradient-warm text-white shadow"><Users className="h-4 w-4" /></span>
                    <div><p className="text-[13px] font-semibold">Manage Students</p><p className="text-xs text-muted-foreground">Applications &amp; status</p></div>
                  </Link>
                  <Link to="/growlearn/register" className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 transition hover:bg-muted">
                    <span className="grid h-9 w-9 place-items-center rounded-[11px] gradient-warm text-white shadow"><UserPlus className="h-4 w-4" /></span>
                    <div><p className="text-[13px] font-semibold">Register New Student</p><p className="text-xs text-muted-foreground">Create account &amp; assign exams</p></div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
      </div>
    </DashboardLayout>
  );
};

export default GrowLearnDashboard;
